import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FixtureData, QuestionData } from "../actions";

/**
 * Custom React hook to handle updating question predictions.
 *
 * Includes:
 * 1. updateQuestions — updates a single question
 * 2. bulkUpdateQuestions — updates multiple questions at once
 */
export const useUpdateQuestions = () => {
  const questionDetails = useSelector((state) => state.questionData.items);
  const questionList = useSelector((state) => state.fixtures.items);
  const dispatch = useDispatch();

  /**
   * Updates a single question.
   */
  const updateQuestions = (questionId, answer) => {
    const updatedPrediction = {
      answer,
      question_id: questionId,
    };

    // Update question details
    const updatedQuestions = questionDetails.map((item) => {
      if (item._id.$oid == questionId) {
        const user_prediction = item.user_prediction
          ? { ...item.user_prediction, ...updatedPrediction }
          : updatedPrediction;
        return { ...item, user_prediction };
      }
      return item;
    });

    // Update fixtures
    const updatedFixtures = questionList.map((item_data) =>
      item_data.map((items) => ({
        ...items,
        data: items?.data?.map((item) => {
          if (item._id.$oid == questionId) {
            const user_prediction = item.user_prediction
              ? { ...item.user_prediction, ...updatedPrediction }
              : updatedPrediction;
            return { ...item, user_prediction };
          }
          return item;
        }),
      })),
    );

    dispatch(FixtureData(updatedFixtures[0]));
    dispatch(QuestionData(updatedQuestions));
  };

  /**
   * Updates multiple questions in one go.
   *
   * @param {Array<{question_id: string, answer: string|number|boolean}>} updates
   * Example: [{ question_id: "123", answer: "Team A" }, { question_id: "456", answer: "Team B" }]
   */
  const bulkUpdateQuestions = (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    // Convert updates array into a map for faster lookup
    const updateMap = updates.reduce((acc, { question_id, answer }) => {
      acc[question_id] = { question_id, answer };
      return acc;
    }, {});

    // Update question details
    const updatedQuestions = questionDetails.map((item) => {
      const update = updateMap[item._id.$oid];
      if (update) {
        const user_prediction = item.user_prediction
          ? { ...item.user_prediction, ...update }
          : update;
        return { ...item, user_prediction };
      }
      return item;
    });

    // Update fixtures
    const updatedFixtures = questionList.map((item_data) =>
      item_data.map((items) => ({
        ...items,
        data: items?.data?.map((item) => {
          const update = updateMap[item._id.$oid];
          if (update) {
            const user_prediction = item.user_prediction
              ? { ...item.user_prediction, ...update }
              : update;
            return { ...item, user_prediction };
          }
          return item;
        }),
      })),
    );

    dispatch(FixtureData(updatedFixtures[0]));
    dispatch(QuestionData(updatedQuestions));
  };

  return { updateQuestions, bulkUpdateQuestions };
};
